// src/components/tenants/TenantOnboarding.tsx
import React, { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { generateClient } from 'aws-amplify/data';
import type { Schema } from '../../../amplify/data/resource';

const client = generateClient<Schema>();

interface TenantOnboardingProps {
    onComplete?: () => void;
}

export default function TenantOnboarding({ onComplete }: TenantOnboardingProps) {
    const queryClient = useQueryClient();
    const [formData, setFormData] = useState({
        name: '',
        domain: '',
        adminEmail: '',
        orgUnitPath: '/',
    });
    const [serviceAccountFile, setServiceAccountFile] = useState<File | null>(null);
    const [useSecretsManager, setUseSecretsManager] = useState(true);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            // Validate it's a JSON file
            if (!file.name.endsWith('.json')) {
                setError('Please upload a valid JSON service account file');
                return;
            }
            setServiceAccountFile(file);
            setError('');
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setSuccess('');

        try {
            // 1. Check for duplicate domain
            const { data: existingTenants } = await client.models.Tenant.list();
            const duplicate = existingTenants.find(
                (t) => t.domain.toLowerCase() === formData.domain.toLowerCase()
            );
            if (duplicate) {
                throw new Error(
                    `A tenant with domain "${formData.domain}" already exists ("${duplicate.name}"). Each domain can only be added once.`
                );
            }

            // 2. Read and parse service account file
            if (!serviceAccountFile) {
                throw new Error('Please upload a service account JSON file');
            }

            const fileContent = await serviceAccountFile.text();
            console.log('File content length:', fileContent.length);
            console.log('First 100 chars:', fileContent.substring(0, 100));

            // Trim any whitespace or BOM characters
            const trimmedContent = fileContent.trim();
            if (!trimmedContent) {
                throw new Error('Service account file is empty');
            }

            let serviceAccount;
            try {
                serviceAccount = JSON.parse(trimmedContent);
            } catch (parseError) {
                console.error('JSON parse error:', parseError);
                console.error('Content that failed to parse:', trimmedContent.substring(0, 200));
                throw new Error('Invalid JSON file. Please ensure you uploaded a valid service account JSON file from Google Cloud.');
            }

            // 2. Validate required fields
            if (!serviceAccount.client_email || !serviceAccount.private_key) {
                throw new Error('Invalid service account file - missing required fields');
            }

            // 3. For now, skip encryption and store credentials directly
            // TODO: Implement encrypt-credentials Lambda function
            console.log('Service account parsed successfully:', {
                project_id: serviceAccount.project_id,
                client_email: serviceAccount.client_email,
            });

            // Temporarily store the JSON as a string
            const encryptData = {
                success: true,
                encryptedCredentials: JSON.stringify(serviceAccount),
                credentialsSecretArn: null,
            };

            // 4. Create tenant in database
            const { data: tenant, errors } = await client.models.Tenant.create({
                name: formData.name,
                domain: formData.domain,
                adminEmail: formData.adminEmail,
                orgUnitPath: formData.orgUnitPath,
                status: 'PENDING',
                credentialsSecretArn: encryptData.credentialsSecretArn,
                encryptedCredentials: encryptData.encryptedCredentials,
                metadata: JSON.stringify({
                    clientEmail: serviceAccount.client_email,
                    projectId: serviceAccount.project_id,
                    onboardedAt: new Date().toISOString(),
                }),
            });

            if (errors) {
                throw new Error(errors[0].message);
            }

            // 5. Verify credentials by testing Google Workspace API connection
            setSuccess('Tenant created! Verifying credentials...');

            try {
                // @ts-ignore - custom mutation
                const verifyResult = await client.mutations.verifyTenantCredentials({
                    tenantId: tenant.id,
                });

                const verificationData = typeof verifyResult.data === 'string'
                    ? JSON.parse(verifyResult.data)
                    : verifyResult.data;

                console.log('Verification result:', verificationData);

                if (verificationData.valid) {
                    // Update tenant status to ACTIVE and store customer ID
                    await client.models.Tenant.update({
                        id: tenant.id,
                        status: 'ACTIVE',
                        customerId: verificationData.customerId,
                    });

                    setSuccess(
                        `Tenant "${formData.name}" successfully onboarded and verified! ` +
                        `Customer ID: ${verificationData.customerId}`
                    );
                } else {
                    // Mark as SUSPENDED if verification failed
                    await client.models.Tenant.update({
                        id: tenant.id,
                        status: 'SUSPENDED',
                    });

                    throw new Error(
                        `Credential verification failed: ${verificationData.error}. ` +
                        `Details: ${verificationData.details || 'Please check Domain-Wide Delegation settings.'}`
                    );
                }

                // Reset form only on success
                setFormData({ name: '', domain: '', adminEmail: '', orgUnitPath: '/' });
                setServiceAccountFile(null);

                // Invalidate tenant list cache so TenantList shows the new tenant
                await queryClient.invalidateQueries({ queryKey: ['tenants'] });

                // Call onComplete callback if provided
                if (onComplete) {
                    setTimeout(onComplete, 2000); // Delay to show success message
                }
            } catch (verifyError: any) {
                // If verification fails, update status to SUSPENDED
                await client.models.Tenant.update({
                    id: tenant.id,
                    status: 'SUSPENDED',
                });

                throw new Error(
                    verifyError.message || 'Credential verification failed. Please check your configuration.'
                );
            }
        } catch (err: any) {
            console.error('Onboarding error:', err);
            setError(err.message || 'Failed to onboard tenant');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="max-w-2xl mx-auto p-6 bg-white rounded-lg shadow-md">
            <h2 className="text-2xl font-bold mb-6 text-gray-800">
                Onboard New Tenant
            </h2>

            {error && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-red-800 text-sm">{error}</p>
                </div>
            )}

            {success && (
                <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-md">
                    <p className="text-green-800 text-sm">{success}</p>
                </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
                {/* Tenant Name */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Tenant Name
                    </label>
                    <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g., Springfield School District"
                    />
                </div>

                {/* Domain */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Google Workspace Domain
                    </label>
                    <input
                        type="text"
                        required
                        value={formData.domain}
                        onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g., springfield.edu"
                    />
                </div>

                {/* Admin Email */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Super Admin Email (for impersonation)
                    </label>
                    <input
                        type="email"
                        required
                        value={formData.adminEmail}
                        onChange={(e) =>
                            setFormData({ ...formData, adminEmail: e.target.value })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g., admin@springfield.edu"
                    />
                    <p className="mt-1 text-sm text-gray-500">
                        This user must have Super Admin privileges in Google Workspace
                    </p>
                </div>

                {/* Org Unit Path */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Default Org Unit Path
                    </label>
                    <input
                        type="text"
                        value={formData.orgUnitPath}
                        onChange={(e) =>
                            setFormData({ ...formData, orgUnitPath: e.target.value })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="/"
                    />
                </div>

                {/* Service Account File */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Service Account JSON File
                    </label>
                    <input
                        type="file"
                        accept=".json"
                        onChange={handleFileUpload}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {serviceAccountFile && (
                        <p className="mt-2 text-sm text-green-600">
                            ✓ {serviceAccountFile.name} loaded
                        </p>
                    )}
                    <p className="mt-1 text-sm text-gray-500">
                        Upload the service-account.json file from Google Cloud Console
                    </p>
                </div>

                {/* Storage Option */}
                <div className="flex items-center space-x-3">
                    <input
                        type="checkbox"
                        id="secretsManager"
                        checked={useSecretsManager}
                        onChange={(e) => setUseSecretsManager(e.target.checked)}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <label htmlFor="secretsManager" className="text-sm text-gray-700">
                        Store credentials in AWS Secrets Manager (Recommended)
                    </label>
                </div>

                {/* Submit Button */}
                <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-blue-600 text-white py-3 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                    {loading ? 'Onboarding...' : 'Onboard Tenant'}
                </button>
            </form>

            {/* Instructions */}
            <div className="mt-8 space-y-4">

                {/* Step-by-step checklist */}
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-md">
                    <h3 className="text-sm font-semibold text-blue-900 mb-2">
                        📋 Before Onboarding — Setup Checklist
                    </h3>
                    <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                        <li>Create a Service Account in the tenant's Google Cloud Console project</li>
                        <li>Enable Domain-Wide Delegation on the service account</li>
                        <li>Enable the four required APIs (see below) in the same GCP project</li>
                        <li>In the tenant's <strong>Google Workspace Admin Console → Security → API Controls → Domain-Wide Delegation</strong>, add the service account Client ID with all eight scopes listed below</li>
                        <li>Download the service-account.json key file and upload it here</li>
                    </ol>
                </div>

                {/* APIs to enable */}
                <div className="p-4 bg-gray-50 border border-gray-200 rounded-md">
                    <h3 className="text-sm font-semibold text-gray-800 mb-2">
                        🔌 Google Cloud APIs to Enable
                    </h3>
                    <ul className="text-sm text-gray-700 space-y-1">
                        <li className="flex items-start gap-2"><span className="text-gray-400 mt-0.5">•</span><span><strong>Admin SDK API</strong> — directory, customer, org unit, and user management</span></li>
                        <li className="flex items-start gap-2"><span className="text-gray-400 mt-0.5">•</span><span><strong>Chrome Policy API</strong> — read and write Chrome OS device/user policies</span></li>
                        <li className="flex items-start gap-2"><span className="text-gray-400 mt-0.5">•</span><span><strong>Google Alert Center API</strong> — sync and archive security alerts</span></li>
                        <li className="flex items-start gap-2"><span className="text-gray-400 mt-0.5">•</span><span><strong>Admin SDK Reports API</strong> — read alert rule activity logs</span></li>
                    </ul>
                </div>

                {/* DWD scopes */}
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-md">
                    <h3 className="text-sm font-semibold text-amber-900 mb-1">
                        🔑 Domain-Wide Delegation Scopes
                    </h3>
                    <p className="text-xs text-amber-700 mb-2">
                        Paste all eight scopes (comma-separated) into the Google Workspace Admin Console when authorizing the service account client ID.
                    </p>
                    <div className="bg-white border border-amber-300 rounded p-2 font-mono text-xs text-gray-700 break-all leading-5 select-all">
                        https://www.googleapis.com/auth/admin.directory.customer.readonly,https://www.googleapis.com/auth/admin.directory.user,https://www.googleapis.com/auth/admin.directory.group,https://www.googleapis.com/auth/admin.directory.orgunit,https://www.googleapis.com/auth/admin.directory.domain,https://www.googleapis.com/auth/chrome.management.policy,https://www.googleapis.com/auth/apps.alerts,https://www.googleapis.com/auth/admin.reports.audit.readonly
                    </div>
                    <table className="mt-3 w-full text-xs text-amber-900">
                        <thead>
                            <tr className="border-b border-amber-200">
                                <th className="text-left pb-1 font-semibold w-1/2">Scope</th>
                                <th className="text-left pb-1 font-semibold">Used for</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-amber-100">
                            <tr><td className="py-1 font-mono pr-2 break-all">admin.directory.customer.readonly</td><td className="py-1">Credential verification on onboarding</td></tr>
                            <tr><td className="py-1 font-mono pr-2 break-all">admin.directory.user</td><td className="py-1">Suspend / force password reset (remediation)</td></tr>
                            <tr><td className="py-1 font-mono pr-2 break-all">admin.directory.group</td><td className="py-1">Group management (future use)</td></tr>
                            <tr><td className="py-1 font-mono pr-2 break-all">admin.directory.orgunit</td><td className="py-1">Fetch org unit list and resolve OU IDs</td></tr>
                            <tr><td className="py-1 font-mono pr-2 break-all">admin.directory.domain</td><td className="py-1">Domain management (future use)</td></tr>
                            <tr><td className="py-1 font-mono pr-2 break-all">chrome.management.policy</td><td className="py-1">Read and push Chrome device/user policies</td></tr>
                            <tr><td className="py-1 font-mono pr-2 break-all">apps.alerts</td><td className="py-1">Sync security alerts and archive on resolve</td></tr>
                            <tr><td className="py-1 font-mono pr-2 break-all">admin.reports.audit.readonly</td><td className="py-1">Read alert rule activity logs</td></tr>
                        </tbody>
                    </table>
                </div>

            </div>
        </div>
    );
}